import "@/assets/css/index.css";
import { changeLanguageDate } from "@/libs/dayjs";
import { changeLanguage } from "@/libs/i18next";
import { PreSelectedRs } from "@/pages/taskpane/features/preSelectedResource/pages";
import HStack from "@components/layout/HStack";
import MainTemplate from "@components/layout/MainTemplate";
import { Loader } from "@components/ui/Loader";
import "@copilotkit/react-ui/styles.css";
import Agent from "@features/agent/pages/Agent";
import FindColleague from "@features/colleagues/pages/FindColleague";
import FindFavorite from "@features/favorites/pages/FindFavorite";
// import QuickSearch from "@features/resources/pages/QuickSearch";  // TODO: Remove quick feature for version 1
import Search from "@features/resources/pages/Search";
import SelectedResources from "@features/resources/pages/SelectedResources";
import Recommendation from "@features/search/pages/Recommendation";
import { ResourceTypeEntity } from "@features/search/services/entities/resourceTypeEntity";
import Setting from "@features/settings/pages/Setting";
import {
  DateTimeFormatEntity,
  DefaultResourceTypeEntity,
  OrganizerSettingEntity,
  TileConfigurationEntity,
} from "@features/settings/services/entities/organizerSettingEntity";
import { useCreateOrganizerSetting } from "@features/settings/services/useCreateOrganizerSetting";
import { useGetOrganizerSetting } from "@features/settings/services/useGetOrganizerSetting";
import queryClient from "@lib/api/query";
import useOfficeTheme from "@lib/office/hooks/useOfficeTheme";
import { useOutlookAppointmentEvents } from "@lib/office/hooks/useOutlookAppointmentEvents";
import { routes } from "@shared/constants/routes";
import { GlobalProvider, useGlobalContext } from "@shared/contexts/GlobalContext";
import { useCheckWarning } from "@shared/hooks/useCheckWarning";
import { useAppointmentStore, useAttendeesStore, useOutlookStore, useResourceStore, useSettingStore } from "@shared/stores";
import { useAppConfigStore } from "@shared/stores/appConfigStore";
import { QueryClientProvider } from "@tanstack/react-query";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom/client";
import { MemoryRouter, Route, Routes } from "react-router-dom";
import LoadingOverLay from "../components/ui/LoadingOverLay";
import { useGetConfiguration } from "../features/settings/services/useGetAppData";
import useOfficeLanguage from "../lib/office/hooks/useOfficeLanguage";
import { SystemSettingKeysEnum } from "../shared/types/enums/AppSettingKeysEnum";
import Start from "./pages/Start";
import { useTaskPane } from "./useTaskPane";
import { usePreSelectedResourceStore } from "../shared/stores/preSelectedResourceStore";
import { EPreSelectedResource } from "../shared/types/enums/preSelectedResourceEnum";
import { msalAdapter } from "@features/auth/services/msalAdapter";
import { useAuthState, useAuthActions } from "@features/auth/store/authStore";
import AuthTest from "@features/test-auth/pages/AuthTest";
import { convertDateFormatToClient, convertTimeFormatToClient } from "../lib/utils/dateTimeHelper";
import { IFilterResource } from "../features/resources/services/types/resourceEntity";
import { EDateSeparator } from "../features/settings/services/entities/enum";

declare const Office: any;

enum AuthState {
  INITIALIZING = "initializing",
  SUCCESS = "success",
  ERROR = "error",
}

const AnimatedRoutesContent = () => {
  const { isLoadingOverlay } = useGlobalContext();
  const { language } = useAppConfigStore();

  useEffect(() => {
    initLanguage(language);
  }, [language]);

  const initLanguage = (lang: string) => {
    changeLanguage("en");
    changeLanguageDate("en");
  };

  return (
    <>
      {isLoadingOverlay && <LoadingOverLay />}
      <div className="tab-transition">
        {/* <Alert /> */}
        <MainTemplate>
          <Routes>
            <Route path={routes.start} element={<Start />} />
            <Route path={routes.recommendation} element={<Recommendation />} />
            <Route path={routes.search} element={<Search />} />
            <Route path={routes.setting} element={<Setting />} />
            <Route path={routes.selectedResources} element={<SelectedResources />} />
            {/* <Route path={routes.changeTime} element={<ChangeTime />} /> */}
            <Route path={routes.findColleague} element={<FindColleague />} />
            <Route path={routes.findFavorite} element={<FindFavorite />} />
            {/* <Route path={routes.quickSearch} element={<QuickSearch />} /> */}
            <Route path={routes.agent} element={<Agent />} />
            <Route path={routes.testAuth} element={<AuthTest />} />
          </Routes>
        </MainTemplate>
        <PreSelectedRs />
      </div>
    </>
  );
};

const AnimatedRoutes = () => {
  return (
    <GlobalProvider>
      <AnimatedRoutesContent />
    </GlobalProvider>
  );
};

const Taskpane: React.FC = () => {
  useOfficeLanguage();
  useOfficeTheme();
  useCheckWarning();
  //useOfficeEvents(); // Handles outlookStore → other stores sync

  useOutlookAppointmentEvents(); // Handles Outlook data → outlookStore

  // Use authStore instead of local state
  const authStateFromStore = useAuthState();
  const { setAuthenticating, setAuthError, setInitialized } = useAuthActions();

  const [isLoading, setIsLoading] = useState(true);
  const { recommendationScreen, capacityFilter } = useSettingStore();
  const {
    selectedResource,
    setMeetingLabel,
    meetingLabel: meetingLabelSelected,
    isCheckSyncResource,
    setFilterResource
  } = useResourceStore();
  const { setConfiguration, meetingLabel, parameters } = useAppConfigStore();
  const {
    requiredAttendees,
    optionalAttendees,
    enhancedLocation,
    organizer,
    organizerName,
    itemId,
    initializeItemId,
    restItemId,
    isReopenAppointment,
    recurringDates,
    iCalUId,
    customUid,
  } = useOutlookStore();
  const { startDateTimeUtc, endDateTimeUtc } = useAppointmentStore();
  const { allAttendees } = useAttendeesStore()

  const { syncAllResources } = useTaskPane();
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Trigger capacityValue cho api filter
  useEffect(() => {
    if (capacityFilter) {
      setFilterResource((prev: IFilterResource | undefined) => ({
        ...(prev ?? {}),
        capacityValue: (allAttendees?.length > 0 ? allAttendees?.length + 1 : 0), // +1 is organizer
      } as IFilterResource));
    }
  }, [allAttendees, capacityFilter]);

  const { items, clearItems, setItems } = usePreSelectedResourceStore();

  // Hàm khởi tạo authentication sử dụng msalAdapter (sẽ tự động set tokens và user vào authStore)
  const initializeAccountManager = async () => {
    try {
      setAuthenticating(true);
      setAuthError(undefined);

      // Lấy thông tin hồ sơ người dùng từ Office API
      const accountType = Office.context.mailbox.userProfile.accountType;
      const loginHint =
        accountType === "office365" || accountType === "outlookCom"
          ? Office.context.mailbox.userProfile.emailAddress
          : "";

      // Khởi tạo msalAdapter - nó sẽ tự động:
      // 1. Initialize AccountManager
      // 2. Lấy user identity và tokens
      // 3. Set tokens và user vào authStore
      await msalAdapter.initialize(loginHint);
      console.log("Authentication initialized successfully - tokens and user data stored in authStore");

      setInitialized(true);
    } catch (initError) {
      console.error("Error initializing authentication:", initError);
      const errorMessage = initError instanceof Error ? initError.message : String(initError);
      setAuthError(`Authentication initialization failed. ${errorMessage}`);
    } finally {
      setAuthenticating(false);
    }
  };

  // Kiểm tra xem có cần authentication không dựa trên URL manifest
  const shouldRequireAuth = useMemo(() => {
    if (typeof window === "undefined") return false;
    const hostname = window.location.hostname;
    return hostname !== "client-app-2.pages.dev";
  }, []);

  // Khởi tạo AccountManager khi component được tạo (chỉ nếu cần authentication)
  useEffect(() => {
    if (shouldRequireAuth) {
      initializeAccountManager();
    } else {
      // Nếu không cần authentication, set initialized = true trong authStore để cho phép truy cập bình thường
      setInitialized(true);
    }
  }, [shouldRequireAuth, setInitialized]);

  const {
    data: organizerSetting,
    error,
    isError,
    isLoading: isLoadingOrganizerSetting,
  } = useGetOrganizerSetting({
    email: organizer || "",
  });
  // const queryClient = useQueryClient();
  // Map all keys from SystemSettingKeysEnum
  const configurationKeys = useMemo(() => {
    return Object.values(SystemSettingKeysEnum);
  }, []);

  const { data: configuration, isLoading: isLoadingConfiguration } = useGetConfiguration({
    payload: {
      parameters: {
        keys: configurationKeys,
      },
    },
  });

  const getDefaultDateTimeFormat = useCallback((dateTimeFormat: DateTimeFormatEntity | null): DateTimeFormatEntity => {
    return new DateTimeFormatEntity({
      firstDayOfWeek: dateTimeFormat?.firstDayOfWeek || parameters?.firstDayOfWeek || 0,
      dateFormat: dateTimeFormat?.dateFormat || convertDateFormatToClient(parameters?.dateFormat) || "DD-MM-YYYY",
      timeFormat: dateTimeFormat?.timeFormat || convertTimeFormatToClient(parameters?.timeFormat) || "HH:mm",
      dateSeparator: dateTimeFormat?.dateSeparator || parameters?.dateSeperator || EDateSeparator.Default,
    });
  }, [parameters]);

  const handleSaveSetting = useCallback((data: OrganizerSettingEntity) => {
    useSettingStore.setState({
      ...data,
      defaultLocations: {
        ...data.defaultLocations,
        defaultResourceTypes: data.defaultLocations.defaultResourceTypes.map((i: DefaultResourceTypeEntity) => ({
          ...i,
          displayName: configuration?.resourceTypes?.find((rt: ResourceTypeEntity) => rt.type === i.type)?.displayName || i.type,
        })),
      },
      ...getDefaultDateTimeFormat(data.dateTimeFormat),
      dateTimeFormat: getDefaultDateTimeFormat(data.dateTimeFormat),
    });
  }, [configuration]);

  const { mutate: createSetting } = useCreateOrganizerSetting({
    mutationConfig: {
      onSuccess: (data: OrganizerSettingEntity) => {
        setIsLoading(false);
        handleSaveSetting(data);
      },
      onError: (error) => {
        console.log("🚨 Error creating setting:", error);
      },
    },
  });

  const isFirstTime = useMemo(
    () => Boolean(!organizerSetting?.defaultResourceType || !organizerSetting?.defaultLocations?.defaultLocationIds),
    [organizerSetting]
  );

  const startRef = useRef<boolean>(true);
  const initRouteRef = useRef<string | null>(null);
  useEffect(() => {
    if (startRef.current && !isCheckSyncResource) {
      startRef.current = false;
    }
  }, [isCheckSyncResource]);
  const initRoute = useMemo(() => {
    if (!startRef.current && initRouteRef.current) {
      return [initRouteRef.current];
    }
    let init = routes.start;
    if (isFirstTime) {
      init = routes.start;
    } else {
      if (!isCheckSyncResource && selectedResource?.length > 0) {
        init = routes.selectedResources;
      } else {
        if (recommendationScreen) {
          init = routes.recommendation;
        } else {
          init = routes.search;
        }
      }
    }
    initRouteRef.current = init;
    return [init];
  }, [isCheckSyncResource, isFirstTime, selectedResource, recommendationScreen]);

  // const initRoute = useMemo(() => {
  //   return isFirstTime
  //     ? [routes.start]
  //     : selectedResource?.length > 0
  //       ? [routes.selectedResources]
  //       : recommendationScreen
  //         ? [routes.recommendation]
  //         : [routes.search];
  // }, [isFirstTime, recommendationScreen, selectedResource]);

  // Initialize Item ID to check if this is a reopened appointment
  useEffect(() => {
    if (organizer) {
      // console.log("🆔 Taskpane: Initializing item ID...");
      initializeItemId();
    }
  }, [organizer, initializeItemId]);

  // Log appointment status for debugging
  useEffect(() => {
    if (itemId !== null || isReopenAppointment !== undefined) {
    }
  }, [itemId, restItemId, isReopenAppointment]);

  // Save Data Configuration to Store
  useEffect(() => {
    if (configuration) {
      setConfiguration(configuration);
      const defaultMeetingLabel = configuration.meetingLabelConfiguration.meetingLabels.find((m) => m.isDisplayDefault);
      if (defaultMeetingLabel && !meetingLabelSelected) {
        setMeetingLabel(defaultMeetingLabel.id);
      }
    }
  }, [configuration, setConfiguration]);

  // Check organizer setting and sync with store
  useEffect(() => {
    // Wait for both queries to have initial data (from cache or API)
    if (isLoadingOrganizerSetting || isLoadingConfiguration) {
      return;
    }

    if (error && isError) {
      const apiError = error as any;
      // Create Organizer Setting if not exist
      if (apiError?.status === 404 && configuration?.resourceTypes) {
        const defaultResourceTypes = configuration?.resourceTypes?.map((i: ResourceTypeEntity) => ({
          type: i.type,
          locationIds: "",
        }));

        const dataBuilder = {
          organizerEmail: organizer || "",
          organizerName: organizerName || "",
          defaultLocations: {
            defaultLocationIds: "",
            defaultResourceTypes: defaultResourceTypes,
          },
          tileConfiguration: new TileConfigurationEntity(),
        };
        createSetting(dataBuilder);
      }
    } else if (organizerSetting && configuration?.resourceTypes) {
      // console.log("✅ Syncing organizer setting to store");
      let isChangeResourceType = false;
      const newDefaultResourceTypes = configuration?.resourceTypes?.map((i: ResourceTypeEntity) => {
        const resourceType = organizerSetting?.defaultLocations?.defaultResourceTypes?.find(
          (resourceType: DefaultResourceTypeEntity) => resourceType.type === i.type
        );
        if (resourceType) {
          return resourceType;
        } else {
          isChangeResourceType = true;
          return {
            type: i.type,
            locationIds: organizerSetting?.defaultLocations?.defaultLocationIds || "",
          };
        }
      });

      // Update Default Resource Types in Organizer Setting if Resource Types changed
      if (
        isChangeResourceType ||
        organizerSetting?.defaultLocations?.defaultResourceTypes?.length !== configuration?.resourceTypes?.length
      ) {
        // console.log("🔄 Updating resource types in organizer setting");
        createSetting({
          organizerEmail: organizer,
          defaultLocations: {
            defaultLocationIds: organizerSetting?.defaultLocations?.defaultLocationIds || "",
            defaultResourceTypes: newDefaultResourceTypes,
          },
        });
      } else {
        // Sync to store
        handleSaveSetting(organizerSetting);
      }
      setIsLoading(false);
    }
  }, [error, isError, organizerSetting, configuration, isLoadingOrganizerSetting, isLoadingConfiguration, parameters]);

  // ✅ Debounced sync with timeout - đảm bảo các dependencies là giá trị cuối cùng
  useEffect(() => {
    if (!meetingLabel || (!iCalUId && !customUid)) {
      return;
    }
    // Clear previous timeout if exists
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    // Set new timeout (500ms debounce) - đảm bảo các giá trị đã ổn định
    debounceTimeoutRef.current = setTimeout(() => {
      syncAllResources();
    }, 600);

    // Cleanup function: clear timeout on unmount or when dependencies change
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
        debounceTimeoutRef.current = null;
      }
    };
  }, [
    JSON.stringify(requiredAttendees),
    JSON.stringify(optionalAttendees),
    JSON.stringify(enhancedLocation),
    JSON.stringify(recurringDates),
    startDateTimeUtc,
    endDateTimeUtc,
    JSON.stringify(meetingLabel),
    iCalUId,
    customUid,
  ]);

  useEffect(() => {
    //clear notify when no selected resource
    if (selectedResource?.length === 0 && items && items.length > 0) {
      const itemsHaveTypeRemoved = items.filter((item) => item.type === EPreSelectedResource.Removed);
      if (itemsHaveTypeRemoved.length === 0) {
        clearItems();
      } else {
        setItems(itemsHaveTypeRemoved);
      }
    }
  }, [JSON.stringify(selectedResource)]);

  // Render authentication state content
  const renderAuthStateContent = () => {
    if (isCheckSyncResource && startRef.current) {
      return (
        <HStack className="w-full h-screen" justifyContent="center" alignItems="center">
          <div className="text-center">
            <Loader texts={["Initializing application..."]} />
          </div>
        </HStack>
      );
    }
    // Nếu không cần authentication, render ứng dụng trực tiếp
    if (!shouldRequireAuth) {
      if (isLoading) {
        return (
          <HStack className="w-full h-screen" justifyContent="center" alignItems="center">
            <Loader texts={["Loading..."]} />
          </HStack>
        );
      }

      return (
        <MemoryRouter initialEntries={initRoute} initialIndex={0}>
          <div className="flex flex-col h-screen">
            <div className="flex-1 overflow-auto content-with-bottom-nav text-darkGray dark:text-white">
              <AnimatedRoutes />
            </div>
          </div>
        </MemoryRouter>
      );
    }

    // Nếu cần authentication, kiểm tra auth state từ authStore
    const { isLoading: isAuthLoading, isInitialized, isAuthenticated, error: authStoreError } = authStateFromStore;

    // Xác định auth state dựa trên authStore
    let currentAuthState: AuthState;

    // Nếu không cần authentication, luôn SUCCESS
    if (!shouldRequireAuth) {
      currentAuthState = AuthState.SUCCESS;
    }
    // Nếu đang trong quá trình initialize (loading hoặc chưa initialized và chưa có error)
    else if (isAuthLoading || (!isInitialized && !authStoreError)) {
      currentAuthState = AuthState.INITIALIZING;
    }
    // Nếu đã initialized và authenticated → SUCCESS
    else if (isInitialized && isAuthenticated) {
      currentAuthState = AuthState.SUCCESS;
    }
    // Nếu có error sau khi đã thử initialize (không loading và có error)
    else if (authStoreError && !isAuthLoading) {
      currentAuthState = AuthState.ERROR;
    }
    // Mặc định là INITIALIZING để hiển thị loading
    else {
      currentAuthState = AuthState.INITIALIZING;
    }

    switch (currentAuthState) {
      case AuthState.INITIALIZING:
        return (
          <HStack className="w-full h-screen" justifyContent="center" alignItems="center">
            <div className="text-center">
              <Loader texts={["Initializing application..."]} />
            </div>
          </HStack>
        );

      case AuthState.ERROR:
        return (
          <HStack className="w-full h-screen" justifyContent="center" alignItems="center">
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded m-4 max-w-md">
              <p className="font-bold">Authentication Error</p>
              <p className="text-sm mt-2">{authStoreError || "Authentication failed"}</p>
              <button
                onClick={initializeAccountManager}
                className="bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded text-sm mt-2"
              >
                Retry
              </button>
            </div>
          </HStack>
        );

      case AuthState.SUCCESS:
        if (isLoading) {
          return (
            <HStack className="w-full h-screen" justifyContent="center" alignItems="center">
              <Loader texts={["Loading..."]} />
            </HStack>
          );
        }

        return (
          <MemoryRouter initialEntries={initRoute} initialIndex={0}>
            <div className="flex flex-col h-screen">
              <div className="flex-1 overflow-auto content-with-bottom-nav text-darkGray dark:text-white">
                <AnimatedRoutes />
              </div>
            </div>
          </MemoryRouter>
        );
    }
  };

  return renderAuthStateContent();
};

// Initialize the component when Office is ready
Office.onReady((info: any) => {
  if (info.host === Office.HostType.Outlook) {
    const root = document.getElementById("taskpane-root");
    if (root) {
      ReactDOM.createRoot(root).render(
        // <React.StrictMode>
        // <PersistQueryClientProvider client={queryClient} persistOptions={{ persister }}>
        <QueryClientProvider client={queryClient}>
          <Taskpane />
        </QueryClientProvider>
        // {/* <ReactQueryDevtools initialIsOpen={false} /> */}
        // </PersistQueryClientProvider>
        // </React.StrictMode>
      );
    }
  }
});

export default Taskpane;
